class DealerNearby extends HTMLElement {
  static FETCH_TIMEOUT = 5000;

  // Returns the nearest location within radius miles, or null.
  static pickDealer(locations, radius) {
    if (!Array.isArray(locations)) return null;
    const inRange = locations.filter((l) => l && l.name && Number(l.distance) <= radius);
    if (inRange.length === 0) return null;
    return inRange.reduce((a, b) => (Number(a.distance) <= Number(b.distance) ? a : b));
  }

  cacheKey() {
    return `dealer-nearby:v1:${this.dataset.stockistAccount}:${this.dataset.radius}`;
  }

  get radius() {
    return Number(this.dataset.radius) || 50;
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    const run = () => this.init().catch(() => {});
    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 4000 });
    } else {
      setTimeout(run, 1500);
    }
  }

  async init() {
    const testGeo = this.testOverride();
    if (!testGeo) {
      const cached = this.readCache();
      if (cached) {
        if (cached.miss) this.offerFallbacks();
        else this.render(cached);
        return;
      }
    }

    const geo = testGeo || (await this.fetchJson('https://get.geojs.io/v1/ip/geo.json'));
    if (!geo || !geo.latitude || !geo.longitude) {
      this.offerFallbacks();
      return;
    }

    const dealer = await this.searchDealers(geo.latitude, geo.longitude);
    if (!dealer) {
      if (!testGeo) this.writeCache({ miss: true });
      this.offerFallbacks();
      return;
    }
    const data = { city: geo.city || 'you', name: dealer.name, distance: Math.max(1, Math.round(dealer.distance)) };
    if (!testGeo) this.writeCache(data);
    this.render(data);
  }

  async searchDealers(latitude, longitude) {
    const url = `https://stockist.co/api/v1/${this.dataset.stockistAccount}/locations/search` +
      `?latitude=${encodeURIComponent(latitude)}&longitude=${encodeURIComponent(longitude)}&distance=${this.radius}`;
    const result = await this.fetchJson(url);
    return DealerNearby.pickDealer(result && result.locations, this.radius);
  }

  render(data) {
    this.querySelector('[data-dn-heading]').textContent = `Dealer near ${data.city}:`;
    this.querySelector('[data-dn-detail]').textContent = `${data.name} · ${data.distance} mi away`;
    this.hideFallbacks();
    this.removeAttribute('hidden');
  }

  setMessage(heading, detail) {
    this.querySelector('[data-dn-heading]').textContent = heading;
    this.querySelector('[data-dn-detail]').textContent = detail;
  }

  // Shown only when IP lookup found no dealer: a ZIP input (works for
  // everyone) plus device geolocation (permission prompt is user-initiated).
  offerFallbacks() {
    const zip = this.querySelector('[data-dn-zip]');
    if (zip) {
      zip.removeAttribute('hidden');
      if (!zip.dnBound) {
        zip.dnBound = true;
        const input = this.querySelector('[data-dn-zip-input]');
        const btn = this.querySelector('[data-dn-zip-btn]');
        btn.addEventListener('click', () => this.searchZip(input, btn).catch(() => {}));
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.searchZip(input, btn).catch(() => {});
          }
        });
      }
    }
    const precise = this.querySelector('[data-dn-precise]');
    if (precise && navigator.geolocation) {
      precise.removeAttribute('hidden');
      if (!precise.dnBound) {
        precise.dnBound = true;
        precise.addEventListener('click', () => this.locateDevice(precise));
      }
    }
  }

  hideFallbacks() {
    const zip = this.querySelector('[data-dn-zip]');
    if (zip) zip.setAttribute('hidden', '');
    const precise = this.querySelector('[data-dn-precise]');
    if (precise) precise.setAttribute('hidden', '');
  }

  async searchZip(input, btn) {
    const zip = input.value.trim();
    if (!/^\d{5}$/.test(zip)) {
      input.focus();
      return;
    }
    btn.disabled = true;
    btn.textContent = '...';
    const geo = await this.fetchJson(`https://api.zippopotam.us/us/${zip}`);
    const place = geo && geo.places && geo.places[0];
    if (!place) {
      this.setMessage("We couldn't find that ZIP", 'Double-check it and try again.');
      btn.disabled = false;
      btn.textContent = 'Find';
      return;
    }
    const dealer = await this.searchDealers(place.latitude, place.longitude);
    if (dealer) {
      const data = { city: place['place name'] || zip, name: dealer.name, distance: Math.max(1, Math.round(dealer.distance)) };
      this.writeCache(data);
      this.render(data);
      return;
    }
    this.setMessage(`No dealer within ${this.radius} miles of ${zip}`, 'Browse the map to find the closest one.');
    btn.disabled = false;
    btn.textContent = 'Find';
  }

  locateDevice(btn) {
    btn.disabled = true;
    btn.textContent = 'Locating...';
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.onDeviceLocation(pos, btn).catch(() => {});
      },
      () => {
        btn.textContent = "Couldn't get your location";
      },
      { timeout: 8000, maximumAge: 300000 }
    );
  }

  async onDeviceLocation(pos, btn) {
    const dealer = await this.searchDealers(pos.coords.latitude, pos.coords.longitude);
    if (dealer) {
      const data = { city: 'you', name: dealer.name, distance: Math.max(1, Math.round(dealer.distance)) };
      this.writeCache(data);
      this.render(data);
      return;
    }
    this.querySelector('[data-dn-heading]').textContent = `No dealer within ${this.radius} miles`;
    this.querySelector('[data-dn-detail]').textContent = 'Browse the map to find the closest one.';
    this.hideFallbacks();
  }

  // ?dealer_test=lat,lng[,City] simulates a location and bypasses the cache.
  testOverride() {
    const param = new URLSearchParams(location.search).get('dealer_test');
    if (!param) return null;
    const [latitude, longitude, city] = param.split(',');
    if (!latitude || !longitude) return null;
    return { latitude, longitude, city: city || 'you' };
  }

  async fetchJson(url) {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), DealerNearby.FETCH_TIMEOUT);
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timer);
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }

  readCache() {
    try {
      return JSON.parse(sessionStorage.getItem(this.cacheKey()));
    } catch {
      return null;
    }
  }

  writeCache(data) {
    try {
      sessionStorage.setItem(this.cacheKey(), JSON.stringify(data));
    } catch {
      // storage unavailable (private mode); skip caching
    }
  }
}

if (!customElements.get('dealer-nearby')) customElements.define('dealer-nearby', DealerNearby);
window.DealerNearby = DealerNearby;
